import { supabaseClient } from './supabase';
import { toast } from './toast';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/candycontent-proxy`;

export interface UniquenessResult {
  percentage: number;
  url?: string;
  matches?: Record<string, string>; // URL -> percentage mapping
  ignored?: string[]; // URLs that were excluded
  details?: any; // Full response details from CandyContent API
}

export async function checkUniqueness(text: string, url?: string | null): Promise<UniquenessResult> {
  const loadingToast = toast.loading('Checking text uniqueness...');

  try {
    if (!text.trim()) {
      toast.error('Text is empty', { id: loadingToast });
      throw new Error('Text is empty');
    }

    const cleanedText = text.trim()
      .replace(/[\r\n]+/g, '\n')
      .replace(/\n\s*\n/g, '\n')
      .replace(/\s+/g, ' ')
      .trim();

    if (cleanedText.length < 100) {
      toast.error('Text must be at least 100 characters long', { id: loadingToast });
      throw new Error('Text must be at least 100 characters long');
    }

    // Get auth session
    const { data: { session } } = await supabaseClient.auth.getSession();
    if (!session) {
      toast.error('Please sign in to check uniqueness', { id: loadingToast });
      throw new Error('Not authenticated');
    }

    console.log('Session exists:', !!session);
    console.log('Access token exists:', !!session.access_token);
    console.log('Access token (first 20 chars):', session.access_token?.substring(0, 20) + '...');
    console.log('ANON KEY exists:', !!import.meta.env.VITE_SUPABASE_ANON_KEY);
    console.log('ANON KEY (first 20 chars):', import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 20) + '...');
    console.log('Sending request to CandyContent Edge Function...');
    console.log('Function URL:', FUNCTION_URL);
    console.log('Request body text length:', cleanedText.length);

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    };
    console.log('Request headers:', Object.keys(headers));

    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ text: cleanedText, url }),
    });

    console.log('CandyContent Edge Function Response Status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    const responseText = await response.text();
    console.log('Raw response:', responseText);

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response as JSON:', e);
      toast.error('Invalid response from server', { id: loadingToast });
      throw new Error('Invalid response from server');
    }
    console.log('CandyContent Edge Function Response:', data);

    if (!response.ok) {
      toast.error(data.error || 'Failed to check uniqueness', { id: loadingToast });
      throw new Error(data.error || 'Failed to check uniqueness');
    }

    // Handle queued status
    if (data.status === 'queued') {
      toast.info(data.message || 'Check queued. Please try again later.', { id: loadingToast });
      return { percentage: 0 };
    }

    // Handle completed status
    if (data.status === 'completed' && typeof data.percentage === 'number') {
      toast.success('Uniqueness check completed', { id: loadingToast });
      return { 
        percentage: data.percentage,
        url: data.url || undefined,
        matches: data.matches || {},
        ignored: data.ignored || [],
        details: data.details
      };
    }

    toast.error('Unexpected response format', { id: loadingToast });
    throw new Error('Unexpected response format');

  } catch (error) {
    console.error('Error checking uniqueness:', error);
    
    if (error instanceof Error && error.message !== 'Text is empty' && error.message !== 'Text must be at least 100 characters long') {
      toast.error(error.message || 'Failed to check uniqueness', { id: loadingToast });
    }
    
    throw error;
  }
}